/**
 * The phone's own summary screen on the TV (2026-09-27, Cole: "fit the stuff
 * we've already built on phone"). The phone publishes its summary as
 * `sections` (protocol.ts TvSummarySection): the same text, putt list, house
 * plot, start-line chart, cards, and scorecards it draws, with its own colors.
 * This page only lays them out side by side in `columns` for 16:9. The plots
 * draw from the phone's shared pure modules (app/LagKing/src/summaryVisuals),
 * so their geometry cannot drift from the phone. The Apple TV app has the
 * React Native twin (web/tv/appletv/src/components/SummaryLayout.tsx).
 */
import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import QRCode from 'qrcode';
import type {
  TvSummary,
  TvSummaryEntriesSection,
  TvSummaryGridSection,
  TvSummaryHandSection,
  TvSummaryHoleLeavesSection,
  TvSummaryHouseSection,
  TvSummaryMatchBoardSection,
  TvSummaryMetricsSection,
  TvSummaryPacePlotSection,
  TvSummaryPuttsSection,
  TvSummarySection,
  TvSummaryStartLinesSection,
  TvSummaryText,
  TvSummaryVersusSection,
} from './protocol';
import { summaryQrUrl } from './protocol';
import PokerCardFace from './PokerCardFace';
import { CountdownRing } from './StatusHints';
import { curlingHouseLayout, houseRingLabelText } from '../../../app/LagKing/src/summaryVisuals/curlingHouse';
import { startLineChartModel } from '../../../app/LagKing/src/summaryVisuals/startLineChart';
import { sim9ScoreBoardModel, sim9ScoreBoardTotals } from '../../../app/LagKing/src/summaryVisuals/sim9MatchBoard';
import { pacePlotGeometry } from '../../../app/LagKing/src/summaryVisuals/paceDispersionPlot';
import {
  centeredInColumn,
  gridRows,
  legendHintText,
  matchBoardPlayerCount,
  matchBoardStrokeRows,
  SUMMARY_CONTINUE_PROMPT,
} from './summaryLayoutRules';
import FitText from './FitText';
import './summaryLayout.css';

export { centeredInColumn, legendHintText, matchBoardPlayerCount, SUMMARY_CONTINUE_PROMPT };

/** True when a summary carries the full layout. */
export function hasSummaryLayout(summary: TvSummary | null | undefined): boolean {
  return Array.isArray(summary?.sections) && summary!.sections!.some((section) => section && typeof section === 'object' && 'kind' in section);
}

export default function SummaryLayout({ summary }: { summary: TvSummary }) {
  const all = (summary.sections ?? []).filter((section): section is TvSummarySection => Boolean(section && section.kind));
  // Full-width rows above and below the columns (a header, a scorecard).
  const topBand = all.filter((section) => section.band === 'top');
  const bottomBand = all.filter((section) => section.band === 'bottom');
  const sections = all.filter((section) => section.band !== 'top' && section.band !== 'bottom');
  const weights = summary.columns?.length ? summary.columns : [1];
  const columns = weights.map((_, index) => sections.filter((section) => (section.column ?? 0) === index));
  // A section pointing past the declared columns joins the last one.
  const overflow = sections.filter((section) => (section.column ?? 0) >= weights.length);
  if (overflow.length) columns[columns.length - 1].push(...overflow);
  const countdownEndsAtMs = summary.countdownEndsAtMs;
  const hasCountdown = typeof countdownEndsAtMs === 'number' && Number.isFinite(countdownEndsAtMs);
  const qrUrl = summaryQrUrl(summary);
  return (
    <section className="summary-layout" aria-live="polite">
      {topBand.length ? (
        <div className="summary-layout-band top">
          {topBand.map((section, index) => <Section section={section} key={`top-${section.kind}-${index}`} />)}
        </div>
      ) : null}
      <div className="summary-layout-columns">
        {columns.map((column, index) => (
          <div className="summary-layout-column" key={index} style={{ flexGrow: weights[index] ?? 1 }}>
            {column.map((section, sectionIndex) => (
              <Section section={centeredInColumn(section, column)} key={`${section.kind}-${sectionIndex}`} />
            ))}
          </div>
        ))}
      </div>
      {bottomBand.length ? (
        <div className="summary-layout-band bottom">
          {bottomBand.map((section, index) => <Section section={section} key={`bottom-${section.kind}-${index}`} />)}
        </div>
      ) : null}
      {/* The phone owns every choice after a round; the layout covers the
          page's sleep footer, so it carries the prompt itself (Cole,
          2026-09-30: "a prompt to continue on your phone"). A card that
          advances by itself shows its ring beside the prompt. */}
      <div className="summary-layout-footer">
        {hasCountdown ? (
          <div className="summary-layout-countdown">
            <CountdownRing endsAtMs={countdownEndsAtMs} label={summary.countdownLabel} />
          </div>
        ) : null}
        <p className="summary-layout-prompt">{SUMMARY_CONTINUE_PROMPT}</p>
        {qrUrl ? <SummaryQr url={qrUrl} label={summary.qr?.label ?? ''} /> : null}
      </div>
    </section>
  );
}


/** A code the room can scan (an event's leaderboard page), with its label. */
function SummaryQr({ url, label }: { url: string; label: string }) {
  const { cells, path } = useMemo(() => {
    const qr = QRCode.create(url, { errorCorrectionLevel: 'M' });
    const size = qr.modules.size;
    let d = '';
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        if (qr.modules.data[y * size + x]) d += `M${x + 2} ${y + 2}h1v1h-1z`;
      }
    }
    return { cells: size + 4, path: d };
  }, [url]);
  return (
    <div className="summary-layout-qr">
      <svg viewBox={`0 0 ${cells} ${cells}`} role="img" aria-label={label || 'QR code'} shapeRendering="crispEdges">
        <rect width={cells} height={cells} fill="#ffffff" />
        <path d={path} fill="#0b1729" />
      </svg>
      {label ? <p>{label}</p> : null}
    </div>
  );
}

/** Plots and charts take the column's spare height; text keeps its own. */
const GROWS: ReadonlySet<TvSummarySection['kind']> = new Set(['house', 'startLines', 'pacePlot']);

function Section({ section }: { section: TvSummarySection }) {
  const body = sectionBody(section);
  const style: CSSProperties = section.border ? { borderColor: section.border } : {};
  return (
    <div
      className={`summary-section summary-section-${section.kind}${section.card ? ' card' : ''}${section.border ? ' bordered' : ''}${GROWS.has(section.kind) ? ' grows' : ''}`}
      style={style}
    >
      {/* A start-line chart carries its title inside its box, so stacked
          charts line up with the plot beside them (Cole, 2026-10-02). */}
      {section.title && section.kind !== 'startLines' ? <p className="summary-section-title">{section.title}</p> : null}
      {body}
    </div>
  );
}

function sectionBody(section: TvSummarySection): ReactNode {
  switch (section.kind) {
    case 'text':
      return <TextLines lines={section.lines} align={section.align} />;
    case 'putts':
      return <PuttList section={section} />;
    case 'house':
      return <HousePlot section={section} />;
    case 'startLines':
      return <StartLineChart section={section} />;
    case 'entries':
      return <Entries section={section} />;
    case 'versus':
      return <Versus section={section} />;
    case 'hand':
      return <Hand section={section} />;
    case 'matchBoard':
      return <MatchBoard section={section} />;
    case 'pacePlot':
      return <PacePlot section={section} />;
    case 'metrics':
      return <Metrics section={section} />;
    case 'holeLeaves':
      return <HoleLeaves section={section} />;
    case 'grid':
      return <Grid section={section} />;
    default:
      return null;
  }
}

/* ---- text ---- */

function textStyle(line: Pick<TvSummaryText, 'color' | 'spacing'>): CSSProperties {
  return {
    ...(line.color ? { color: line.color } : {}),
    ...(line.spacing != null ? { letterSpacing: `${line.spacing}em` } : {}),
  };
}

export function TextLines({ lines, align = 'center' }: { lines: TvSummaryText[] | null | undefined; align?: 'center' | 'left' }) {
  if (!lines?.length) return null;
  return (
    <div className={`summary-lines align-${align}`}>
      {lines.map((line, index) => {
        if (line.divider) return <hr className="summary-divider" key={index} />;
        const text = (
          <span className={`summary-text size-${line.size ?? 'body'} weight-${line.weight ?? 'regular'}`} style={textStyle(line)}>
            {line.text}
          </span>
        );
        if (line.rightText == null) return <div className="summary-line" key={index}>{text}</div>;
        return (
          <div className="summary-line summary-line-row" key={index}>
            {text}
            <span
              className={`summary-text size-${line.rightSize ?? 'body'} weight-${line.rightWeight ?? 'heavy'}`}
              style={line.rightColor ? { color: line.rightColor } : undefined}
            >
              {line.rightText}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ---- putt by putt ---- */

function PuttList({ section }: { section: TvSummaryPuttsSection }) {
  return (
    <div className="summary-putts">
      {section.header ? (
        <div className="summary-putts-header">
          <span>{section.header.left}</span>
          <strong>{section.header.right}</strong>
        </div>
      ) : null}
      {section.rows.map((row, index) => (
        <div className="summary-putts-row" key={`${row.index}-${index}`}>
          <span className="summary-putts-index">{row.index}</span>
          <span className="summary-putts-distance">
            <strong style={{ color: row.color }}>{row.primary}</strong>
            {row.secondary ? <small style={{ color: row.color }}>{row.secondary}</small> : null}
          </span>
          <span className="summary-putts-line" style={{ color: row.lineColor }}>{row.line}</span>
        </div>
      ))}
    </div>
  );
}

/* ---- measured square plots ---- */

function useBoxSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setSize((current) =>
        Math.round(current.width) === Math.round(rect.width) && Math.round(current.height) === Math.round(rect.height)
          ? current
          : { width: rect.width, height: rect.height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, size] as const;
}

function HousePlot({ section }: { section: TvSummaryHouseSection }) {
  const [ref, box] = useBoxSize<HTMLDivElement>();
  const side = Math.floor(Math.min(box.width, box.height));
  const hint = legendHintText(section.legendHint);
  return (
    <div className="summary-plot">
      {section.legend?.length ? (
        // Readable at ten feet: a swatch and a name per player, centered over
        // the plot (Cole, 2026-09-30: "legend is too small and not clear").
        <div className="summary-legend">
          {section.legend.map((item, index) => (
            <span key={index}>
              <i style={{ background: item.color }} />
              <b style={{ color: item.color }}>{item.text}</b>
            </span>
          ))}
        </div>
      ) : null}
      <div className="summary-plot-area" ref={ref}>
        {side > 40 ? <HouseSvg section={section} size={side} /> : null}
      </div>
      {hint ? <p className="summary-plot-hint" style={section.legendColor ? { color: section.legendColor } : undefined}>{hint}</p> : null}
      {section.caption?.length ? (
        <div className="summary-plot-caption"><TextLines lines={section.caption} /></div>
      ) : null}
    </div>
  );
}


export function HouseSvg({ section, size }: { section: TvSummaryHouseSection; size: number }) {
  const layout = curlingHouseLayout({
    balls: section.balls,
    width: size,
    height: size,
    lateralExaggeration: section.lateralExaggeration,
    headroomFactor: section.headroomFactor,
    minimumExtentFt: section.minimumExtentFt,
    showRingLabels: section.showRingLabels,
    ringLabelPosition: section.ringLabelPosition,
    edgePaddingPx: section.edgePaddingPx,
    referenceRings: section.rings,
    defaultRingColor: section.palette.ring,
  });
  const { scale, cx, cy } = layout;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Where they landed">
      <line x1={cx} y1={size} x2={cx} y2={cy} stroke={section.palette.line} strokeWidth={1.5 * scale} strokeDasharray={`${6 * scale},${6 * scale}`} strokeOpacity={0.7} />
      {layout.rings.map((ring) => (
        <circle key={ring.radiusFt} cx={cx} cy={cy} r={ring.r} stroke={ring.stroke} strokeWidth={ring.strokeWidth} strokeOpacity={0.9} fill={ring.fill} fillOpacity={ring.fillOpacity} />
      ))}
      {section.showRingLabels
        ? layout.ringLabels.map((label) => (
            <text key={`l-${label.radiusFt}`} x={label.x} y={label.y} fill={section.palette.line} fontSize={layout.labelFontPx} textAnchor={label.anchor}>
              {houseRingLabelText(label.radiusFt, section.unitPerFt, section.unitLabel)}
            </text>
          ))
        : null}
      <line x1={cx - 8 * scale} y1={cy} x2={cx + 8 * scale} y2={cy} stroke={section.palette.pin} strokeWidth={2 * scale} />
      <line x1={cx} y1={cy - 8 * scale} x2={cx} y2={cy + 8 * scale} stroke={section.palette.pin} strokeWidth={2 * scale} />
      {section.balls.map((ball, index) => {
        const { x, y } = layout.toPxXY(ball.longitudinalMissFt, ball.lateralMissFt);
        return (
          <g key={index}>
            {ball.halo ? <circle cx={x} cy={y} r={layout.haloRadiusPx} stroke={ball.color} strokeWidth={1.5} strokeOpacity={0.7} fill="none" /> : null}
            <circle cx={x} cy={y} r={layout.ballRadiusPx} fill={ball.color} stroke={section.palette.outline} strokeWidth={1.5} />
            <text x={x} y={y + layout.labelFontPx * 0.36} fill={section.palette.outline} fontSize={layout.labelFontPx} fontWeight={700} textAnchor="middle">
              {ball.order}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ---- start lines ---- */

function StartLineChart({ section }: { section: TvSummaryStartLinesSection }) {
  const model = startLineChartModel(section.angles ?? [], section.scaleMaxCount ?? undefined);
  const palette = section.palette;
  return (
    <div className="summary-chart" style={{ background: palette.empty }}>
      {section.title ? <p className="summary-section-title summary-chart-title">{section.title}</p> : null}
      <div className="summary-chart-bars">
        {model.bars.map((bar) => {
          const tone = palette[bar.toneKey];
          return (
            <div className="summary-chart-col" key={bar.category}>
              <div className="summary-chart-track">
                <span className="summary-chart-count" style={{ color: palette.count }}>{bar.count}</span>
                <i
                  className="summary-chart-bar"
                  style={bar.count === 0 ? { height: 4, background: palette.empty } : { height: `${bar.heightFraction * 100}%`, background: tone }}
                />
              </div>
              {/* Two-line labels (the phone's compact chart): seven one-line
                  labels do not fit a TV column at reading size. */}
              <span className="summary-chart-label" style={{ color: tone }}>{bar.compactLabel}</span>
            </div>
          );
        })}
      </div>
      <div className="summary-chart-axis" style={{ color: palette.axis }}>
        <span>LEFT</span>
        <i />
        <span>RIGHT</span>
      </div>
      {model.classified === 0 ? <p className="summary-chart-empty" style={{ color: palette.muted }}>No start-line data captured.</p> : null}
    </div>
  );
}

/* ---- entries, versus, metrics ---- */

function Entries({ section }: { section: TvSummaryEntriesSection }) {
  return (
    <div className="summary-entries">
      {section.rows.map((row, index) => (
        <div className="summary-entry" key={`${row.name}-${index}`} style={row.border ? { borderColor: row.border } : undefined}>
          {row.rank != null ? <span className="summary-entry-rank" style={row.rankColor ? { color: row.rankColor } : undefined}>{row.rank}</span> : null}
          <span className="summary-entry-main">
            <strong style={row.nameColor ? { color: row.nameColor } : undefined}>{row.name}</strong>
            {row.detail ? <small style={row.detailColor ? { color: row.detailColor } : undefined}>{row.detail}</small> : null}
          </span>
          <span className="summary-entry-value">
            <strong style={row.valueColor ? { color: row.valueColor } : undefined}>{row.value}</strong>
            {row.valueLabel ? <small>{row.valueLabel}</small> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function Versus({ section }: { section: TvSummaryVersusSection }) {
  return (
    <div className="summary-versus">
      {section.boxes.map((box, index) => (
        <div className="summary-versus-item" key={`${box.label}-${index}`}>
          {index > 0 ? <span className="summary-versus-sep">{section.separator}</span> : null}
          <div className="summary-versus-box" style={box.border ? { borderColor: box.border } : undefined}>
            {box.label ? <span className="summary-versus-label">{box.label}</span> : null}
            {box.handle ? <span className="summary-versus-handle">{box.handle}</span> : null}
            <strong style={{ color: box.valueColor }}>{box.value}</strong>
            {box.unit ? <span className="summary-versus-unit">{box.unit}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function Metrics({ section }: { section: TvSummaryMetricsSection }) {
  const perRow = Math.max(1, section.perRow ?? 2);
  return (
    <div className="summary-metrics" style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}>
      {section.tiles.map((tile, index) => (
        <div className="summary-metric" key={`${tile.label}-${index}`}>
          <span style={tile.labelColor ? { color: tile.labelColor } : undefined}>{tile.label}</span>
          <strong style={tile.valueColor ? { color: tile.valueColor } : undefined}>{tile.value}</strong>
          {tile.sub ? <small style={tile.subColor ? { color: tile.subColor } : undefined}>{tile.sub}</small> : null}
        </div>
      ))}
    </div>
  );
}

/* ---- cards ---- */

function Hand({ section }: { section: TvSummaryHandSection }) {
  return (
    <div className="summary-hand">
      <TextLines lines={section.lines} />
      <div className="summary-hand-cards">
        {section.cards.length ? (
          section.cards.map((card, index) => (
            <span className="summary-hand-card" key={index} style={section.dim?.[index] ? { opacity: 0.35 } : undefined}>
              <PokerCardFace card={card} />
            </span>
          ))
        ) : (
          section.empty ? <em>{section.empty}</em> : null
        )}
      </div>
      <TextLines lines={section.after} />
    </div>
  );
}

/* ---- per-hole leaves (Poker challenge) ---- */

function HoleLeaves({ section }: { section: TvSummaryHoleLeavesSection }) {
  return (
    <div className="summary-holes">
      {section.holes.map((hole, index) => (
        <div className="summary-hole" key={index}>
          <span style={{ color: section.labelColor }}>{hole.label}</span>
          <strong>
            <b style={{ color: section.colorA }}>{hole.a}</b>
            {hole.b != null && hole.b !== '' ? (
              <>
                <i style={{ color: section.dividerColor }}> / </i>
                <b style={{ color: section.colorB }}>{hole.b}</b>
              </>
            ) : null}
          </strong>
        </div>
      ))}
    </div>
  );
}

/* ---- Sim 9 match board ---- */

function MatchBoard({ section }: { section: TvSummaryMatchBoardSection }) {
  // One row per player the phone names: one for a solo round or a sent
  // challenge, two for a match, up to six for stroke play.
  const rows = matchBoardStrokeRows(section);
  const players = matchBoardPlayerCount(section);
  const holes = sim9ScoreBoardModel(section.holes, section.pars, rows);
  const totals = sim9ScoreBoardTotals(holes);
  const p = section.palette;
  const label = (name: string) => name.slice(0, 6).toUpperCase();
  return (
    <div className={`summary-board-table${players > 2 ? ' many' : ''}`} style={{ borderColor: p.line }}>
      <div className="summary-board-col labels">
        <span style={{ color: p.label }}>HOLE</span>
        <span style={{ color: p.label }}>FT</span>
        <span style={{ color: p.label }}>PAR</span>
        {section.names.slice(0, players).map((name, index) => (
          <span className="score" style={{ color: section.nameColors?.[index] ?? p.label }} key={index}>{label(name ?? '')}</span>
        ))}
      </div>
      {holes.map((hole) => (
        <div className="summary-board-col" key={hole.number} style={{ borderColor: p.line }}>
          <span className="hole" style={{ color: p.hole }}>{hole.number}</span>
          <span style={{ color: p.ft }}>{hole.distanceFt}</span>
          <span style={{ color: p.par }}>{hole.par}</span>
          {hole.cells.map((cell, index) => (
            <span className="score" key={index}>
              <span className="summary-board-score" style={cell.won ? { background: p.wonFill } : undefined}>
                {cell.strokes == null ? (
                  <b style={{ color: p.label }}>·</b>
                ) : (
                  <b
                    className={cell.mark?.shape ? `mark ${cell.mark.shape}${cell.mark.doubled ? ' doubled' : ''}` : undefined}
                    style={{ color: cell.won ? p.won : p.score }}
                  >
                    {cell.strokes}
                  </b>
                )}
              </span>
            </span>
          ))}
        </div>
      ))}
      {/* The round's totals, match play or not (Cole, 2026-10-02). */}
      <div className="summary-board-col total" style={{ borderColor: p.line }}>
        <span className="hole" style={{ color: p.label }}>TOT</span>
        <span style={{ color: p.ft }}>{totals.distanceFt}</span>
        <span style={{ color: p.par }}>{totals.par}</span>
        {totals.strokes.map((total, index) => (
          <span className="score" key={index}>
            <span className="summary-board-score">
              <b style={{ color: total == null ? p.label : p.score }}>{total == null ? '·' : total}</b>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---- game board grid (Line Four, Turf War) ---- */

function Grid({ section }: { section: TvSummaryGridSection }) {
  const rows = gridRows(section);
  const p = section.palette;
  const shape = section.shape === 'disc' ? 'disc' : 'box';
  const columns = Math.max(1, Math.floor(section.columns || 1));
  return (
    <div className={`summary-grid ${shape}`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, borderColor: p.line }}>
      {section.columnLabels?.length
        ? section.columnLabels.slice(0, columns).map((text, index) => (
            <span className="summary-grid-label" key={`label-${index}`} style={{ color: p.label }}>{text}</span>
          ))
        : null}
      {rows.flatMap((row, rowIndex) =>
        row.map((cell, index) => (
          <span className="summary-grid-cell" key={`${rowIndex}-${index}`}>
            <span
              className="summary-grid-fill"
              style={{
                background: cell.fill ?? p.empty,
                ...(cell.ring ? { boxShadow: `0 0 0 4px ${cell.ring}` } : {}),
              }}
            >
              {cell.text ? <FitText text={cell.text} style={cell.textColor ? { color: cell.textColor } : undefined} /> : null}
            </span>
            {cell.sub ? <small style={{ color: p.label }}>{cell.sub}</small> : null}
          </span>
        )),
      )}
    </div>
  );
}

/* ---- pace & dispersion ---- */

function PacePlot({ section }: { section: TvSummaryPacePlotSection }) {
  const [ref, box] = useBoxSize<HTMLDivElement>();
  const side = Math.floor(Math.min(box.width, box.height));
  return (
    <div className="summary-plot">
      <div className="summary-plot-area" ref={ref}>
        {side > 40 ? <PaceSvg section={section} size={side} /> : null}
      </div>
      {section.footnote ? <div className="summary-plot-caption"><TextLines lines={[section.footnote]} /></div> : null}
    </div>
  );
}

function PaceSvg({ section, size }: { section: TvSummaryPacePlotSection; size: number }) {
  const g = pacePlotGeometry(
    {
      points: section.points ?? [],
      goalFraction: section.goalFraction,
      ellipse: section.ellipse ?? null,
      centroid: section.centroid ?? null,
    },
    size,
  );
  const p = section.palette;
  const { center, plotRadius } = g;
  const labelProps = { fill: p.label, fontSize: g.labelSize, fontWeight: 700 } as const;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Projected pace and dispersion plot">
      <circle cx={center} cy={center} r={plotRadius} fill={p.disc} stroke={p.ring} strokeWidth={1.5} />
      <line x1={center - plotRadius} y1={center} x2={center + plotRadius} y2={center} stroke={p.ring} strokeWidth={1} />
      <line x1={center} y1={center - plotRadius} x2={center} y2={center + plotRadius} stroke={p.ring} strokeWidth={1} />
      <circle cx={center} cy={center} r={g.goalRadius} fill={p.goalFill} stroke={p.goal} strokeWidth={2} />
      {g.ellipse ? (
        <ellipse
          cx={g.ellipse.cx}
          cy={g.ellipse.cy}
          rx={g.ellipse.rx}
          ry={g.ellipse.ry}
          fill={p.ellipseFill}
          stroke={p.ellipse}
          strokeWidth={2.5}
          strokeDasharray="7 5"
          transform={`rotate(${-g.ellipse.rotationDeg} ${g.ellipse.cx} ${g.ellipse.cy})`}
        />
      ) : null}
      {g.points.map((point, index) => (
        <circle
          key={index}
          cx={point.x}
          cy={point.y}
          r={g.pointRadius}
          fill={point.isMake ? p.make : point.first ? p.first : p.dot}
          fillOpacity={point.isMake || point.first ? 1 : 0.75}
          stroke={p.outline}
          strokeWidth={1.25}
        />
      ))}
      {g.centroid ? (
        <>
          <line x1={g.centroid.x - 7} y1={g.centroid.y} x2={g.centroid.x + 7} y2={g.centroid.y} stroke={p.ellipse} strokeWidth={3} />
          <line x1={g.centroid.x} y1={g.centroid.y - 7} x2={g.centroid.x} y2={g.centroid.y + 7} stroke={p.ellipse} strokeWidth={3} />
        </>
      ) : null}
      <circle cx={center} cy={center} r={3} fill={p.ellipse} />
      <text x={g.labels.long.x} y={g.labels.long.y} textAnchor="middle" {...labelProps}>LONG</text>
      <text x={g.labels.short.x} y={g.labels.short.y} textAnchor="middle" {...labelProps}>SHORT</text>
      <text x={g.labels.left.x} y={g.labels.left.y} textAnchor="start" {...labelProps}>LEFT</text>
      <text x={g.labels.right.x} y={g.labels.right.y} textAnchor="end" {...labelProps}>RIGHT</text>
    </svg>
  );
}
